import React, { useState, useEffect } from "react";
import { useOrderChangesDialog } from "../../Components/orders/useOrderChangesDialog";
import OrderChangeImagesButton from "../../Components/orders/OrderChangeImagesButton";
import { useQuery } from "@tanstack/react-query";
import {
  checkDuplicatePhoneCount,
  fetchPrintletWorkflowOrders,
  fetchPrintletWorkflowOrdersSummary,
  updateOrderStatus,
  updateOrderStatusBulk,
  printletWorkflowBulkSend,
  deleteOrder,
} from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { readyOrderUnits,formatPhoneWithCaseStyle, teePlusPackedItemData } from "../../utils/orderLineDisplay";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import ViewConversationButton from "../../Components/orders/ViewConversationButton";
import EditOrderModal from "./EditOrderModal";
import { copyOrderToTeePlus } from "../../utils/productionSource";
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  Package,
  User,
  Phone,
  MapPin,
  MapIcon,
  Copy,
  Download,
  LinkIcon,
  Edit,
  Trash2,
  Archive,
  Clock,
  Filter,
  Search,
  Loader,
  CheckSquare,
} from "./printletWorkflowIcons";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }
  return null;
};

const workflowMark = (order) => {
  if (order.sentToPrintlet || order.printletExternalOrderId) return "sent";
  if (order.printletOutcome === "failed" || (order.printletSendError && !order.sentToPrintlet)) return "failed";
  if (order.printletDupSkipped || order.duplicatePhoneCount > 0) return "duplicate";
  if (order.printletOutcome === "not_sent") return "notSent";
  return "ready";
};

const markBadge = (order) => {
  const mark = workflowMark(order);
  if (mark === "sent") {
    return {
      label: order.printletSentThisSession ? "Sent ✓" : "Sent",
      className: order.printletSentThisSession
        ? "text-white bg-blue-600 border-blue-700"
        : "text-blue-900 bg-blue-200 border-blue-400",
      title: order.printletExternalOrderId
        ? `Printlet #${order.printletExternalOrderId}`
        : "مُرسل إلى Printlet",
    };
  }
  if (mark === "failed") {
    return {
      label: "Failed",
      className: "text-white bg-rose-600 border-rose-700",
      title: order.printletSendError || "فشل الإرسال",
    };
  }
  if (mark === "duplicate") {
    return {
      label: "DUP",
      className: "text-amber-950 bg-amber-300 border-amber-500",
      title: `تكرار رقم — ${order.duplicatePhoneCount || "?"} طلب سابق`,
    };
  }
  if (mark === "notSent") {
    return {
      label: "Not Sent",
      className: "text-slate-800 bg-slate-200 border-slate-400",
      title: "لم يُرسل",
    };
  }
  return {
    label: "Ready",
    className: "text-emerald-950 bg-emerald-300 border-emerald-500",
    title: "جاهز للإرسال",
  };
};

const PrintletWorkflowOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const { changesDialog, openOrderChanges } = useOrderChangesDialog(async (savedOrderId) => {
    setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== savedOrderId));
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      next.delete(savedOrderId);
      return next;
    });
    toast.success("تم تحديث الطلب إلى Changes بنجاح");
  });
  
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(true);
  const [workflowFilter, setWorkflowFilter] = useState("all");
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isSendingPrintlet, setIsSendingPrintlet] = useState(false);
  const [actionNotice, setActionNotice] = useState(null);

  const { data: printletOrdersSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["printletWorkflowOrdersSummary"],
    queryFn: fetchPrintletWorkflowOrdersSummary,
    enabled: canViewOrders(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      toast.error("فشل تحميل الملف");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).catch(() => {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    });
    toast.success("تم النسخ");
  };

  const handleCopyPhoneNumber = (phoneNumber) => copyToClipboard(phoneNumber);

  const enrichOrders = async (orders) => {
    return Promise.all(
      orders.map(async (order) => {
        let enrichedItems = [];
        for (const detail of order.orderDetails || []) {
          const readyUnits = await readyOrderUnits(detail);
          if (readyUnits) {
            enrichedItems.push(...readyUnits);
            continue;
          }
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;
          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code?.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
            code: design.code || "None",
          };
          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({ ...baseItem, quantityIndex: i + 1, totalQuantity: detail.quantity });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          changeNoteImageCount: Number(order.changeNoteImageCount || 0),
          status: order.status || "Printlet",
          sentToPrintlet: !!order.sentToPrintlet,
          printletExternalOrderId: order.printletExternalOrderId || "",
          printletSendError: order.printletSendError || "",
          duplicatePhoneCount: order.duplicatePhoneCount || 0,
          printletOutcome: order.sentToPrintlet ? "sent" : "",
          printletDupSkipped: false,
          printletSentThisSession: false,
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchPrintletWorkflowOrders(0, pageSize);
      setHasMore(!(data.length === 0 || data.length < pageSize));
      setLoadedOrders(await enrichOrders(data));
      setCurrentPage(1);
      setSelectedOrderNumbers(new Set());
    } catch (err) {
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchPrintletWorkflowOrders(currentPage, pageSize);
      if (data.length === 0 || data.length < pageSize) setHasMore(false);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setIsOrdersLoading(true);
    try {
      const data = await fetchPrintletWorkflowOrders(0, newSize);
      setLoadedOrders(await enrichOrders(data));
      setCurrentPage(1);
      setHasMore(data.length === newSize);
      setSelectedOrderNumbers(new Set());
    } catch (err) {
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";
    if (newStatus === "Old") {
      if (!window.confirm("هل تريد تحديث الطلب إلى قديم؟")) return;
    } else if (newStatus === "Changes") {
      openOrderChanges(orderId);
      return;
    } else if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (!window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`)) {
      return;
    }

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success(`تم تحديث الطلب إلى ${newStatus}`);
    } catch (err) {
      toast.error("فشل التحديث: " + (err.message || "خطأ غير معروف"));
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`)) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`تم حذف الطلب رقم ${orderId}`);
    } catch (err) {
      toast.error("فشل في حذف الطلب.");
    }
  };

  const toggleSelectOrder = (orderNumber) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const setSelectAllVisible = (checked, visibleOrders) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (checked) visibleOrders.forEach((o) => next.add(o.orderNumber));
      else visibleOrders.forEach((o) => next.delete(o.orderNumber));
      return next;
    });
  };

  const selectedOrders = () => loadedOrders.filter((o) => selectedOrderNumbers.has(o.orderNumber));

  const applySendResults = (results, { sessionSent = false } = {}) => {
    const byId = new Map((results || []).map((r) => [Number(r.orderId), r]));
    setLoadedOrders((prev) =>
      prev.map((order) => {
        const row = byId.get(Number(order.orderNumber));
        if (!row) return order;
        const outcome = row.outcome;
        if (outcome === "success") {
          const warn = (row.warnings || []).filter(Boolean).join(" ");
          return {
            ...order,
            sentToPrintlet: true,
            printletExternalOrderId: row.printletOrderId || order.printletExternalOrderId,
            printletSendError: warn,
            printletOutcome: "sent",
            printletDupSkipped: false,
            printletSentThisSession: sessionSent,
          };
        }
        if (outcome === "already_sent") {
          return {
            ...order,
            sentToPrintlet: true,
            printletExternalOrderId: row.printletOrderId || order.printletExternalOrderId,
            printletSendError: row.error || order.printletSendError,
            printletOutcome: "sent",
          };
        }
        if (outcome === "duplicate") {
          return {
            ...order,
            printletOutcome: "duplicate",
            printletDupSkipped: true,
            duplicatePhoneCount: row.duplicateCount || order.duplicatePhoneCount,
            printletSendError: row.error || order.printletSendError,
          };
        }
        return {
          ...order,
          printletOutcome: "failed",
          printletSendError: row.error || "Printlet send failed.",
        };
      })
    );
  };

  const handleSendSelectedToPrintlet = async () => {
    if (!canAct) return;
    const selected = selectedOrders();
    if (selected.length === 0) {
      setActionNotice({ type: "info", text: "اختر أوردرات أولاً" });
      return;
    }

    const alreadySent = selected.filter((o) => o.sentToPrintlet || o.printletExternalOrderId);
    const toSend = selected.filter((o) => !o.sentToPrintlet && !o.printletExternalOrderId);
    if (toSend.length === 0) {
      setActionNotice({
        type: "info",
        text: "كل الأوردرات المحددة مُرسلة إلى Printlet بالفعل",
      });
      return;
    }

    if (!window.confirm(
      `إرسال ${toSend.length} طلب(ات) إلى Printlet؟\nسيتم تأكيد COD والدفع من المحفظة تلقائياً.\nلن يتم إرسال المتكرر تلقائياً.`
    )) return;

    setIsSendingPrintlet(true);
    setActionNotice({
      type: "info",
      text:
        alreadySent.length > 0
          ? `جاري الإرسال إلى Printlet (${alreadySent.length} هتتخطى لأنها مُرسلة)... لا تغلق الصفحة`
          : "جاري الإرسال إلى Printlet... لا تغلق الصفحة",
    });

    const formatFailures = (results) =>
      (results || [])
        .filter((r) => r.outcome === "failed")
        .slice(0, 8)
        .map((r) => `#${r.orderId}: ${r.error || "فشل"}`)
        .join("\n");
    const formatWarnings = (results) =>
      (results || [])
        .flatMap((r) => (r.warnings || []).map((w) => `#${r.orderId}: ${w}`))
        .slice(0, 8)
        .join("\n");

    try {
      const first = await printletWorkflowBulkSend(toSend.map((o) => o.orderNumber));
      applySendResults(first.results, { sessionSent: true });
      const dups = (first.results || []).filter((r) => r.outcome === "duplicate");
      const success = first.success || 0;
      const failed = first.failed || 0;
      const already = first.alreadySent || 0;
      const payWarn = formatWarnings(first.results);
      let notice = {
        type: failed > 0 ? "error" : payWarn ? "info" : "success",
        text: `نجاح ${success} · فشل ${failed} · مُرسل مسبقاً ${already} · متكرر ${dups.length}${payWarn ? " — راجع تحذير الدفع" : " · COD والمحفظة تلقائي"}`,
        detail: [formatFailures(first.results), payWarn].filter(Boolean).join("\n"),
      };

      if (dups.length > 0) {
        const dupList = dups
          .slice(0, 15)
          .map((r) => {
            const order = toSend.find((o) => o.orderNumber === r.orderId);
            return `#${r.orderId} — ${order?.name || ""} (${r.duplicateCount || "?"} سابق / تكرار رقم)`;
          })
          .join("\n");
        const sendDuplicates = window.confirm(
          `فيه ${dups.length} طلب(ات) متكررة مراحتش لـ Printlet.\n\n${dupList}` +
            (dups.length > 15 ? "\n..." : "") +
            `\n\nهل عايز تبعتها برضو؟`
        );
        if (!sendDuplicates) {
          setActionNotice({
            ...notice,
            text: `${notice.text} — تم تخطي المتكرر`,
          });
          return;
        }
        setActionNotice({ type: "info", text: "جاري إرسال المتكرر بعد التأكيد..." });
        const second = await printletWorkflowBulkSend(
          dups.map((r) => r.orderId),
          dups.map((r) => r.orderId)
        );
        applySendResults(second.results, { sessionSent: true });
        const secondFailed = second.failed || 0;
        notice = {
          type: failed > 0 || secondFailed > 0 ? "error" : "success",
          text: `${notice.text} | المتكرر: نجاح ${second.success || 0} · فشل ${secondFailed} · مُرسل مسبقاً ${second.alreadySent || 0}`,
          detail: [formatFailures(first.results), formatFailures(second.results)].filter(Boolean).join("\n"),
        };
      }

      setActionNotice(notice);
    } catch (err) {
      setActionNotice({
        type: "error",
        text: err.message || "فشل الإرسال الجماعي إلى Printlet",
      });
    } finally {
      setIsSendingPrintlet(false);
    }
  };

  const handleSendSelectedToOld = async () => {
    if (!canAct) return;
    const selected = selectedOrders();
    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }
    if (!window.confirm(`هل تريد تحويل ${selected.length} طلب(ات) محددة إلى "قديم"؟`)) return;

    setIsBulkUpdating(true);
    try {
      const ids = selected.map((o) => o.orderNumber);
      const result = await updateOrderStatusBulk(ids, "Old", "تم التحديث الجماعي من طابور Printlet");
      const okIds = new Set((result.results || []).filter((r) => r.ok).map((r) => r.orderId));
      setLoadedOrders((prev) => prev.filter((o) => !okIds.has(o.orderNumber)));
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        okIds.forEach((id) => next.delete(id));
        return next;
      });
      toast.success(`تم تحويل ${okIds.size} طلب(ات) إلى قديم`);
    } catch (err) {
      toast.error(err.message || "فشل التحويل إلى قديم");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleCopyAllExcelData = async (order) => {
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );
      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) return;
      }
      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (D)`;
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;
      const lines = [];
      order.items.forEach((item, index) => {
        const itemLine = `${index + 1}-${order.items.length}${teePlusPackedItemData(item)}`;
        lines.push(index === 0 ? `${customerBase}\t\t${itemLine}` : `\t\t\t\t\t\t\t\t\t${itemLine}`);
      });
      await copyOrderToTeePlus(lines.join("\n"), order.orderNumber);
    } catch (err) {
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error("فشل في النسخ");
    }
  };

  const handleOrderEdited = async (updatedOrder) => {
    if (!updatedOrder?.orderId) {
      await loadInitialOrders();
      return;
    }
    try {
      const [enriched] = await enrichOrders([updatedOrder]);
      setLoadedOrders((prev) =>
        prev.map((o) => (o.orderNumber === enriched.orderNumber ? { ...o, ...enriched } : o))
      );
    } catch (err) {
      await loadInitialOrders();
    }
  };

  const filteredOrders = loadedOrders.filter((order) => {
    const mark = workflowMark(order);
    if (workflowFilter === "ready" && mark !== "ready") return false;
    if (workflowFilter === "sent" && mark !== "sent") return false;
    if (workflowFilter === "duplicate" && mark !== "duplicate") return false;
    if (workflowFilter === "failed" && mark !== "failed") return false;
    if (workflowFilter === "notSent" && mark === "sent") return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  const filterCounts = {
    all: loadedOrders.length,
    ready: loadedOrders.filter((o) => workflowMark(o) === "ready").length,
    sent: loadedOrders.filter((o) => workflowMark(o) === "sent").length,
    duplicate: loadedOrders.filter((o) => workflowMark(o) === "duplicate").length,
    failed: loadedOrders.filter((o) => workflowMark(o) === "failed").length,
    notSent: loadedOrders.filter((o) => workflowMark(o) !== "sent").length,
  };

  const busy = isOrdersLoading || isBulkUpdating || isSendingPrintlet;

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      {changesDialog}
      <ToastContainer position="top-center" icon={false} rtl />

      {editingOrderId && (
        <EditOrderModal
          orderId={editingOrderId}
          onClose={() => setEditingOrderId(null)}
          onSaved={handleOrderEdited}
        />
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Package className="text-fuchsia-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-fuchsia-600 to-pink-600 bg-clip-text text-transparent">
              طلبات Printlet
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {(error || errorSummary) && (
          <div className="bg-red-100 text-red-700 p-4 rounded-xl mb-6 border border-red-200">
            {error || "فشل في تحميل بيانات طلبات Printlet."}
          </div>
        )}

        {actionNotice && (
          <div
            className={`p-4 rounded-xl mb-6 border-2 whitespace-pre-wrap ${
              actionNotice.type === "error"
                ? "bg-rose-100 text-rose-900 border-rose-400"
                : actionNotice.type === "success"
                  ? "bg-emerald-100 text-emerald-900 border-emerald-400"
                  : "bg-sky-100 text-sky-900 border-sky-400"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <p className="font-bold">{actionNotice.text}</p>
              <button
                type="button"
                onClick={() => setActionNotice(null)}
                className="text-sm font-bold opacity-70 hover:opacity-100"
              >
                إغلاق
              </button>
            </div>
            {actionNotice.detail ? (
              <p className="mt-2 text-sm font-medium">{actionNotice.detail}</p>
            ) : null}
          </div>
        )}

        {isAdmin(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 border border-white/30">
              <h2 className="text-xl font-bold text-gray-800 mb-3">ملخص طابور Printlet</h2>
              {isLoadingSummary ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-fuchsia-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-fuchsia-600">{printletOrdersSummary.count}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{printletOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {canAct && (
          <div className="bg-gradient-to-r from-fuchsia-50 to-pink-50 backdrop-blur-sm rounded-2xl shadow-xl p-4 md:p-6 mb-6 border-2 border-fuchsia-300">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-fuchsia-900 text-lg">إجراءات المحدد</h3>
                <p className="text-sm text-fuchsia-700">
                  المحدد: <span className="font-bold">{selectedOrderNumbers.size}</span> طلب
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSelectAllVisible(true, filteredOrders)}
                  disabled={filteredOrders.length === 0 || busy}
                  className="bg-white border-2 border-fuchsia-400 text-fuchsia-800 font-bold px-5 py-3 rounded-xl hover:bg-fuchsia-50 disabled:opacity-50 flex items-center gap-2"
                >
                  <CheckSquare size={18} />
                  تحديد الظاهر
                </button>
                <button
                  type="button"
                  onClick={() => setSelectAllVisible(false, filteredOrders)}
                  disabled={selectedOrderNumbers.size === 0 || busy}
                  className="bg-white border-2 border-gray-300 text-gray-700 font-bold px-5 py-3 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                >
                  إلغاء التحديد
                </button>
                <button
                  type="button"
                  onClick={handleSendSelectedToPrintlet}
                  disabled={selectedOrderNumbers.size === 0 || busy}
                  className="bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl flex items-center gap-2"
                >
                  {isSendingPrintlet ? (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                      <path d="m21.854 2.147-10.94 10.939" />
                    </svg>
                  )}
                  {isSendingPrintlet
                    ? "جاري الإرسال..."
                    : `إرسال المحدد لـ Printlet (${selectedOrderNumbers.size})`}
                </button>
                <button
                  type="button"
                  onClick={handleSendSelectedToOld}
                  disabled={selectedOrderNumbers.size === 0 || busy}
                  className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl flex items-center gap-2"
                >
                  <Archive size={18} />
                  تحويل المحدد للقديم
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-fuchsia-300 focus:border-fuchsia-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50"
            >
              <Filter size={20} />
              تصفية
            </button>
          </div>
          {showFilters && (
            <div className="flex flex-wrap gap-2 mt-4">
              {[
                { key: "all", label: "الكل", count: filterCounts.all, active: "bg-indigo-600 text-white border-indigo-600" },
                { key: "ready", label: "Ready to Send", count: filterCounts.ready, active: "bg-emerald-500 text-white border-emerald-600" },
                { key: "sent", label: "Sent", count: filterCounts.sent, active: "bg-blue-600 text-white border-blue-700" },
                { key: "duplicate", label: "Duplicate", count: filterCounts.duplicate, active: "bg-amber-400 text-amber-950 border-amber-500" },
                { key: "failed", label: "Failed", count: filterCounts.failed, active: "bg-rose-600 text-white border-rose-700" },
                { key: "notSent", label: "Not Sent", count: filterCounts.notSent, active: "bg-slate-600 text-white border-slate-700" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setWorkflowFilter(opt.key)}
                  className={`px-4 py-2 rounded-xl border-2 font-bold text-sm ${
                    workflowFilter === opt.key ? opt.active : "bg-white text-gray-700 border-gray-200"
                  }`}
                >
                  {opt.label} ({opt.count})
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl bg-white"
                disabled={isOrdersLoading}
              >
                <option value={10}>10 طلبات</option>
                <option value={20}>20 طلب</option>
                <option value={30}>30 طلب</option>
                <option value={40}>40 طلب</option>
                <option value={50}>50 طلب</option>
                <option value={80}>80 طلب</option>
                <option value={100}>100 طلب</option>
              </select>
              <span className="text-xl text-gray-600">(إجمالي المعروض: {loadedOrders.length})</span>
            </div>
            {isOrdersLoading && (
              <div className="flex items-center gap-2 text-fuchsia-600">
                <Loader className="animate-spin" size={20} />
                <span>جاري التحميل...</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : filteredOrders.length > 0 ? (
            <>
              <table className="w-full min-w-[1200px] table-auto">
                <thead>
                  <tr className="bg-gradient-to-r from-fuchsia-50 to-pink-50 border-b-2 border-fuchsia-200">
                    <th className="p-2 text-center font-bold text-fuchsia-800">
                      <div className="flex flex-col items-center gap-1">
                        <input
                          type="checkbox"
                          className="w-5 h-5 accent-fuchsia-600"
                          checked={
                            filteredOrders.length > 0 &&
                            filteredOrders.every((o) => selectedOrderNumbers.has(o.orderNumber))
                          }
                          onChange={(e) => setSelectAllVisible(e.target.checked, filteredOrders)}
                        />
                        <span className="text-xs">الطلب</span>
                      </div>
                    </th>
                    <th className="p-4 text-right font-bold text-fuchsia-800">العميل</th>
                    <th className="p-4 text-right font-bold text-fuchsia-800">العنوان</th>
                    <th className="p-4 text-center font-bold text-fuchsia-800">الإجمالي</th>
                    <th className="p-4 text-right font-bold text-fuchsia-800">المنتجات + الرابط</th>
                    <th className="p-4 text-right font-bold text-fuchsia-800">ملاحظات</th>
                    <th className="p-4 text-center font-bold text-fuchsia-800">إجراءات</th>
                    <th className="p-4 text-center font-bold text-fuchsia-800">حذف</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const badge = markBadge(order);
                    return (
                      <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60">
                        <td className="p-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="checkbox"
                              className="w-5 h-5 accent-fuchsia-600"
                              checked={selectedOrderNumbers.has(order.orderNumber)}
                              onChange={() => toggleSelectOrder(order.orderNumber)}
                            />
                            <span className="font-mono font-bold text-fuchsia-700 text-sm">#{order.orderNumber}</span>
                            <span
                              title={badge.title}
                              className={`text-[10px] font-bold rounded px-1.5 py-0.5 border ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                            {order.printletSendError && !order.sentToPrintlet && (
                              <span className="text-[10px] text-red-700 max-w-[140px] leading-tight">
                                {order.printletSendError}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          {isAdmin(authState.role) && (
                            <button
                              onClick={() => setEditingOrderId(order.orderNumber)}
                              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium mb-3"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Edit size={14} />
                                تعديل
                              </div>
                            </button>
                          )}
                          <div className="flex items-center gap-2">
                            <User size={16} className="text-gray-500" />
                            <span style={{ fontFamily: "cairo black" }} className="text-lg">{order.name}</span>
                          </div>
                          <div className="space-y-1 mt-2">
                            <ViewConversationButton
                              order={order}
                              phone={order.phoneNumber}
                              templatePreset="search"
                              canSend={canAct}
                              compact
                              className="w-full"
                            />
                            <button
                              type="button"
                              onClick={() => handleCopyPhoneNumber(order.phoneNumber)}
                              className="flex items-center gap-1 text-green-600"
                            >
                              <Phone size={14} />
                              {order.phoneNumber}
                            </button>
                          </div>
                        </td>
                        <td className="p-4 max-w-[200px]">
                          <div className="flex items-start gap-2">
                            <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                            <span style={{ fontFamily: "cairo black" }} className="text-[13px] line-clamp-5">
                              {order.address}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <MapIcon size={16} className="text-gray-500" />
                            <span className="text-2xl text-purple-600">{order.state}</span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                        </td>
                        <td className="p-4">
                          {order.items.map((item, index) => (
                            <div key={`${item.id}-${index}`} className="mb-3">
                              <div className="font-medium text-indigo-700">{item.name}</div>
                              <div style={{ fontFamily: "cairo black" }} className="text-[13px]">{item.type}</div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">{item.code}</span>
                                {item.driveLink && (
                                  <a href={item.driveLink} target="_blank" rel="noopener noreferrer" className="text-indigo-600">
                                    <LinkIcon size={16} />
                                  </a>
                                )}
                                {item.code.startsWith("CUSTOM") && item.driveLink && (
                                  <button
                                    onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                    className="text-green-600"
                                  >
                                    <Download size={16} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </td>
                        <td className="p-2 max-w-[150px]">
                          <p style={{ fontFamily: "cairo black" }} className="text-lg text-gray-600 line-clamp-3">
                            {order.note || "—"}
                          </p>
                          <OrderChangeImagesButton orderId={order.orderNumber} count={order.changeNoteImageCount} />
                        </td>
                        <td className="p-4">
                          {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleCopyAllExcelData(order)}
                                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={16} />
                                نسخ (TeePlus)
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg text-sm"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-500 text-white px-3 py-2 rounded-lg text-sm"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Clock size={14} />
                                  معلق
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                className="bg-purple-500 text-white px-3 py-2 rounded-lg text-sm"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Edit size={14} />
                                  تغييرات
                                </div>
                              </button>
                            </div>
                          ) : (
                            <p className="text-center text-sm text-slate-500">عرض فقط</p>
                          )}
                        </td>
                        <td className="p-4">
                          {canAct ? (
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg text-sm"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                            </div>
                          ) : (
                            <p className="text-center text-sm text-slate-500">عرض فقط</p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {hasMore && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMoreOrders}
                    disabled={isLoadingMore}
                    className="bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white px-8 py-4 rounded-xl font-bold disabled:opacity-50 flex items-center gap-2 mx-auto"
                  >
                    {isLoadingMore ? (
                      <>
                        <Loader className="animate-spin" size={20} />
                        جاري التحميل...
                      </>
                    ) : (
                      <>
                        <ChevronLeft size={20} />
                        تحميل المزيد
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <Package className="mx-auto mb-4 text-gray-400" size={64} />
              <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات في طابور Printlet</p>
            </div>
          )}
        </div>

        <div className="fixed bottom-6 right-6">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white p-4 rounded-full shadow-2xl"
          >
            <ChevronUp size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintletWorkflowOrdersPage;
